import { normalizeActivityType } from './workoutActivity';

/** Map HealthKit workout names/ids to Preformiq activity types. */
export function mapHealthKitWorkout(
  workoutActivityName: string | undefined,
  workoutActivityId?: number,
): { activityType: string; name: string } {
  const raw = String(workoutActivityName ?? '').trim();
  const key = raw.toLowerCase();

  const byName: Record<string, { activityType: string; label: string }> = {
    running: { activityType: 'outdoor_run', label: 'Run' },
    run: { activityType: 'outdoor_run', label: 'Run' },
    walking: { activityType: 'outdoor_walk', label: 'Walk' },
    walk: { activityType: 'outdoor_walk', label: 'Walk' },
    hiking: { activityType: 'hike', label: 'Hike' },
    hike: { activityType: 'hike', label: 'Hike' },
    cycling: { activityType: 'outdoor_ride', label: 'Ride' },
    'outdoor cycling': { activityType: 'outdoor_ride', label: 'Ride' },
    'indoor cycling': { activityType: 'spin_class', label: 'Indoor cycle' },
    swimming: { activityType: 'swim', label: 'Swim' },
    swim: { activityType: 'swim', label: 'Swim' },
    elliptical: { activityType: 'elliptical', label: 'Elliptical' },
    rowing: { activityType: 'rower', label: 'Row' },
    'stair climbing': { activityType: 'stair_climber', label: 'Stairs' },
    stairs: { activityType: 'stair_climber', label: 'Stairs' },
    yoga: { activityType: 'yoga', label: 'Yoga' },
    pilates: { activityType: 'pilates', label: 'Pilates' },
    'high intensity interval training': { activityType: 'hiit', label: 'HIIT' },
    hiit: { activityType: 'hiit', label: 'HIIT' },
    dance: { activityType: 'dance', label: 'Dance' },
    'traditional strength training': { activityType: 'weightlifting', label: 'Strength' },
    'functional strength training': { activityType: 'weightlifting', label: 'Strength' },
    'core training': { activityType: 'hiit', label: 'Core' },
    treadmill: { activityType: 'treadmill', label: 'Treadmill' },
    'cross training': { activityType: 'other_cardio', label: 'Cross training' },
    basketball: { activityType: 'sports', label: 'Basketball' },
    soccer: { activityType: 'sports', label: 'Soccer' },
    tennis: { activityType: 'sports', label: 'Tennis' },
  };

  const hit = byName[key];
  if (hit) {
    return { activityType: normalizeActivityType(hit.activityType), name: raw || hit.label };
  }

  const byId: Record<number, { activityType: string; label: string }> = {
    37: { activityType: 'outdoor_run', label: 'Run' },
    52: { activityType: 'outdoor_walk', label: 'Walk' },
    13: { activityType: 'outdoor_ride', label: 'Ride' },
    46: { activityType: 'swim', label: 'Swim' },
    24: { activityType: 'hike', label: 'Hike' },
    20: { activityType: 'weightlifting', label: 'Strength' },
    63: { activityType: 'hiit', label: 'HIIT' },
  };
  if (workoutActivityId != null && byId[workoutActivityId]) {
    const m = byId[workoutActivityId];
    return { activityType: normalizeActivityType(m.activityType), name: raw || m.label };
  }

  return {
    activityType: normalizeActivityType('other_cardio'),
    name: raw || 'Apple Health workout',
  };
}

/**
 * Capacitor HealthKit plugin returns workout `duration` in **hours**
 * (start/end interval / 3600). Prefer start/end when available.
 */
export function durationMinutesFromHealth(
  durationHours: number | undefined,
  startIso: string | undefined,
  endIso: string | undefined,
): number {
  if (startIso && endIso) {
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    if (Number.isFinite(ms) && ms > 0) {
      return Math.min(1440, Math.max(1, Math.round(ms / 60000)));
    }
  }
  if (durationHours != null && Number.isFinite(durationHours) && durationHours > 0) {
    // Plugin sends hours; values > 24 would be absurd for a single workout session.
    const minutes = Math.round(durationHours * 60);
    return Math.min(1440, Math.max(1, minutes));
  }
  return 15;
}
